import { AxiosStatic } from 'axios';
import { Store } from 'redux';
import { API_DOMAIN } from '../../app/constants';
import { actions } from '../../app/store/ducks/auth.duck';
import { IAppState, TAppActions } from '../../app/store/rootDuck';
import { REFRESH_TOKEN_URL } from '../../app/constants';
export function removeCSSClass(ele: HTMLElement, cls: string) {
  const reg = new RegExp('(\\s|^)' + cls + '(\\s|$)');
  ele.className = ele.className.replace(reg, ' ');
}

export function addCSSClass(ele: HTMLElement, cls: string) {
  ele.classList.add(cls);
}

export const toAbsoluteUrl = (pathname: string) => process.env.PUBLIC_URL + pathname;

export function setupAxios(
  axios: AxiosStatic,
  store: Store<IAppState, TAppActions>,
) {
  axios.defaults.baseURL = API_DOMAIN;
  
  // Request interceptor
  axios.interceptors.request.use(
    config => {
      if (!config.baseURL || config.baseURL === API_DOMAIN) {
        const {
          auth: { authToken },
        } = store.getState();

        if (authToken) {
          config.headers.Authorization = `Bearer ${authToken}`;
        }
        config.headers['X-App-Type'] = 'admin';
      }
      return config;
    },
    err => Promise.reject(err)
  );

  // Response interceptor
  axios.interceptors.response.use(undefined, async error => {
    if (!error?.response) {
      const {
        auth: { emptyResErrAttempts },
      } = store.getState();

      if (emptyResErrAttempts >= 3) {
        store.dispatch(actions.setEmptyResErrAttempts(0));
        return Promise.reject(error);
      }

      return new Promise(async resolve => {
        await new Promise(r => setTimeout(r, 1000));
        store.dispatch(actions.setEmptyResErrAttempts(emptyResErrAttempts + 1));
        resolve(Promise.resolve(axios.request(error.config)));
      });
    } else {
      // Handle 403 error (authentication failed)
      if (
        error?.response?.status === 403 &&
        (error?.response?.message === 'Authentication failed, no access' ||
          error?.response?.data?.message === 'Authentication failed, no access')
      ) {
        store.dispatch(actions.logout());
      }

      if (error.response.status === 401) {
        const originalRequest = error.config;

        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const state = store.getState();
          const refreshToken = state.auth.refreshToken;
          
          if (refreshToken) {
            try {
              const { data: response } = await axios.post(REFRESH_TOKEN_URL, { refreshToken });

              if (response?.accessToken) {
                store.dispatch(actions.setAuthToken(response.accessToken));
                originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
                return axios(originalRequest);
              } else {
                store.dispatch(actions.logout());
                return Promise.reject(error);
              }
            } catch (err) {
              store.dispatch(actions.logout());
              return Promise.reject(err);
            }
          } else {
            store.dispatch(actions.logout());  
            return Promise.reject(error);
          }
        }
      }
      return Promise.reject(error);
    }
  });
}


/*  removeStorage: removes a key from localStorage and its sibling expiracy key
    params:
        key <string>     : localStorage key to remove
    returns:
        <boolean> : telling if operation succeeded
 */
export function removeStorage(key: string) {
  try {
    localStorage.setItem(key, '');
    localStorage.setItem(key + '_expiresIn', '');
  } catch (e) {
    console.log(
      'removeStorage: Error removing key [' + key + '] from localStorage: ' + JSON.stringify(e)
    );
    return false;
  }
  return true;
}

/*  getStorage: retrieves a key from localStorage previously set with setStorage().
    params:
        key <string> : localStorage key
    returns:
        <string> : value of localStorage key
        null : in case of expired key or failure
 */
export function getStorage(key: string) {
  const now = Date.now(); //epoch time, lets deal only with integer
  // set expiration for storage
  let expiresIn: string | null | number = localStorage.getItem(key + '_expiresIn');
  if (expiresIn === undefined || expiresIn === null) {
    expiresIn = 0;
  }

  expiresIn = Math.abs(+expiresIn);
  if (expiresIn < now) {
    // Expired
    removeStorage(key);
    return null;
  } else {
    try {
      const value = localStorage.getItem(key);
      return value;
    } catch (e) {
      console.log(
        'getStorage: Error reading key [' + key + '] from localStorage: ' + JSON.stringify(e)
      );
      return null;
    }
  }
}
/*  setStorage: writes a key into localStorage setting a expire time
    params:
        key <string>     : localStorage key
        value <string>   : localStorage value
        expires <number> : number of seconds from now to expire the key
    returns:
        <boolean> : telling if operation succeeded
 */
export function setStorage(key: string, value: string, expires?: number) {
  if (expires === undefined || expires === null) {
    expires = 24 * 60 * 60; // default: seconds for 1 day
  }

  const now = Date.now(); //millisecs since epoch time, lets deal only with integer
  const schedule = now + expires * 1000;
  try {
    localStorage.setItem(key, value);
    localStorage.setItem(key + '_expiresIn', JSON.stringify(schedule));
  } catch (e) {
    console.log(
      'setStorage: Error setting key [' + key + '] in localStorage: ' + JSON.stringify(e)
    );
    return false;
  }
  return true;
}
